import { IconArrowLeft } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import X from "@/assets/icons/x.svg";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import type { MultidietCateringsType } from "@/features/multidiet/types/multidiet-caterings.type";
import type { MultidietSetupVariantType } from "@/features/multidiet-picker/types/multidiet-setup.type";
import { formatCateringName } from "@/lib/helpers/format-brand-name";
import { cn } from "@/lib/utils";

type MultidietCateringsModalProps = {
  trigger: React.ReactNode;
  variant: MultidietSetupVariantType;
  data: MultidietCateringsType[];
  layout?: "page" | "popup";
};

export const MultidietCateringsModal = ({
  trigger,
  variant,
  data,
  layout,
}: MultidietCateringsModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations();

  return (
    <Dialog modal={true} onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={cn(
          "z-[99] flex h-svh max-h-svh max-w-screen flex-col gap-0 rounded-none p-0 md:h-[calc(100svh-100px)] md:max-h-[770px] md:overflow-hidden md:rounded-xl lg:min-w-[900px]",
        )}
        closeButton={false}
        overlayClassName={layout === "popup" ? "hidden" : ""}
      >
        <div className="flex h-[70px] w-full items-center justify-between border-b bg-brand-white p-4">
          <div className="flex items-center gap-4">
            <Button
              className="m-0 min-h-0 p-0"
              onClick={() => setIsOpen(false)}
              variant="ghost"
            >
              <IconArrowLeft className="size-7" strokeWidth={2} />
            </Button>
            {/* Desktop */}
            <p className="font-medium text-2xl max-md:hidden">
              {t("multidiet-picker.caterings")}
            </p>
          </div>
          {/* Mobile */}
          <p className="font-medium text-xl md:hidden">
            {t("multidiet-picker.caterings")}
          </p>
          <DialogClose>
            <X />
          </DialogClose>
        </div>
        <DialogHeader className="overflow-hidden bg-brand-grey4 px-4 py-4 text-left md:px-8">
          <DialogTitle className="font-medium text-xl leading-6 md:text-2xl">
            {t(`multidiet-picker.caterings-variants-names.${variant.type}`)}
          </DialogTitle>
          <p>
            {t.rich("multidiet-picker.price-per-day", {
              price: (chunks) => <span>{chunks}</span>,
              value: variant.price,
            })}
          </p>
        </DialogHeader>
        <ScrollArea className="p-4 max-md:h-[calc(100svh-150px)] md:max-h-[600px] md:p-6">
          <div className="grid grid-cols-3 gap-4 md:grid-cols-5 md:gap-6">
            {data.map((catering) => {
              return (
                <div
                  className="flex flex-1 flex-col items-center gap-2"
                  key={catering.id}
                >
                  <ListingItemCateringImage
                    className="pointer-events-none flex h-[110px] w-full border-brand-grey1 p-2 shadow-none md:h-[126px] [&>img]:w-[80%] md:[&>img]:w-[80%]"
                    imageUrl={catering?.imageUrl || ""}
                    name={formatCateringName(catering?.name || "")}
                  />
                  <p className="text-center font-medium text-sm">
                    {formatCateringName(catering.name)}
                  </p>
                </div>
              );
            })}
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
